// Schema registry model (PLAN §2 "Profile templates", §4 "Schema registry"). Admin-configurable types, fields,
// templates, value lists and relation types. The DB rows (packages/db) and the API payload use these shapes.

export const ENTITY_KINDS = ['actor', 'event', 'location', 'asset', 'document', 'case'] as const;
export type EntityKind = (typeof ENTITY_KINDS)[number];
export const isEntityKind = (k: unknown): k is EntityKind => ENTITY_KINDS.includes(k as EntityKind);

export const ENTITY_KIND_LABELS: Record<EntityKind, string> = {
  actor: 'Actor',
  event: 'Event',
  location: 'Place',
  asset: 'Asset / Transaction',
  document: 'Source',
  case: 'Case',
};

/** Field data types (PLAN §4 `field_defs.data_type`). */
export const FIELD_TYPES = [
  'text',
  'longtext',
  'number',
  'fuzzy_date',
  'enum',
  'multi_enum',
  'url',
  'email',
  'phone',
  'money_range',
  'rating',
  'table',
  'social_handles',
  'entity_ref',
  'place_ref',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];
export const isFieldType = (t: unknown): t is FieldType => FIELD_TYPES.includes(t as FieldType);

/** Table columns may also point at events. Nested tables are not allowed. */
export const COLUMN_TYPES = [...FIELD_TYPES.filter(t => t !== 'table'), 'event_ref'] as const;
export type ColumnType = (typeof COLUMN_TYPES)[number];
export const isColumnType = (t: unknown): t is ColumnType => COLUMN_TYPES.includes(t as ColumnType);

export const FIELD_TYPE_INFO: Record<FieldType | 'event_ref', { label: string; help: string }> = {
  text: { label: 'Short text', help: 'One line of text in any language.' },
  longtext: { label: 'Long text', help: 'Paragraphs: analysis, descriptions, narratives.' },
  number: { label: 'Number', help: 'A plain number.' },
  fuzzy_date: {
    label: 'Date (fuzzy)',
    help: 'A date with its precision: a day, month, year, range, "since 2021", "c. 2021". The original text is kept.',
  },
  enum: { label: 'Choice from a list', help: 'One value from an admin-editable list (e.g. Camp).' },
  multi_enum: { label: 'Several choices from a list', help: 'Any number of values from an admin-editable list.' },
  url: { label: 'Web address', help: 'A link starting with http:// or https://.' },
  email: { label: 'Email address', help: 'An email address.' },
  phone: { label: 'Phone number', help: 'A phone number, ideally with the country code (+33 …).' },
  money_range: {
    label: 'Amount (exact, range or undisclosed)',
    help: 'An amount with currency: exact ("€50,000"), a range ("€2–10M"), an estimate ("est. $1M") or "undisclosed".',
  },
  rating: {
    label: 'Rating with justification',
    help: 'A value on the rating scale (Low … High, or 1–5), stored as 1–5 with the original wording and a justification.',
  },
  table: { label: 'Table', help: 'Rows with their own columns (e.g. funding sources, key events, network links).' },
  social_handles: { label: 'Social media handles', help: 'Accounts per platform, e.g. "X: @name | Facebook: page".' },
  entity_ref: { label: 'Link to an entity', help: 'Points at an Actor (or other entity) in the graph.' },
  place_ref: { label: 'Link to a place', help: 'Points at a Place (country, city, venue, address).' },
  event_ref: { label: 'Link to an event', help: 'Points at an Event in the graph.' },
};

export const DEFAULT_CLAIM_KINDS = ['fact', 'assessment'] as const;
export type DefaultClaimKind = (typeof DEFAULT_CLAIM_KINDS)[number];

export interface TableColumn {
  key: string;
  label: string;
  type: ColumnType;
  /** For enum / multi_enum columns. */
  enumList?: string | null;
  multiValued?: boolean;
  required?: boolean;
  help?: string;
}

export interface EntityTypeDef {
  id: string;
  kind: EntityKind;
  key: string;
  label: string;
  description: string | null;
  icon: string | null;
  /** Entity palette slot 1–12 (tokens.ts); null inherits the parent's slot (sub-types get a tint of it). */
  colorSlot: number | null;
  parentTypeId: string | null;
  /** ID code prefix, e.g. `MED_` (the importer pre-selects the type from it). */
  codePrefix: string | null;
  order: number;
  archived: boolean;
}

export interface FieldGroupDef {
  id: string;
  entityTypeId: string;
  key: string;
  label: string;
  order: number;
}

export interface FieldDef {
  id: string;
  groupId: string;
  key: string;
  label: string;
  dataType: FieldType;
  multilingual: boolean;
  multiValued: boolean;
  required: boolean;
  /** Value list key for enum / multi_enum. */
  enumList: string | null;
  /** Value list key holding the rating scale (for `rating`). */
  ratingScale: string | null;
  justificationRequired: boolean;
  /** Columns for `table`. */
  columns: TableColumn[] | null;
  claimKind: DefaultClaimKind;
  help: string | null;
  placeholder: string | null;
  formatHint: string | null;
  order: number;
  archived: boolean;
}

export const BLOCK_LAYOUTS = ['header', 'fields', 'table', 'ratings', 'text'] as const;
export type BlockLayout = (typeof BLOCK_LAYOUTS)[number];

/** Built-in entity properties a block can show besides its fields (they live on the entity row, not in field_defs). */
export const BLOCK_BUILTINS = ['code', 'display_name', 'type', 'reliability', 'updated'] as const;
export type BlockBuiltin = (typeof BLOCK_BUILTINS)[number];

/** One block of a profile template: drives the profile page, the importer and the DOCX/PDF export. */
export interface TemplateBlock {
  key: string;
  /** Printed number ("BLOCK 4"), if the sheet numbers its blocks. */
  number?: number | null;
  label: string;
  /** Field group shown in this block. */
  group?: string | null;
  builtins?: BlockBuiltin[];
  order: number;
  /** What the block becomes in the graph (for help and the importer), e.g. "Flow claims". */
  storedAs?: string | null;
  layout: BlockLayout;
  /** Ignored on import and never shown (Block 9 composite score, PLAN §1). */
  ignored?: boolean;
  import?: {
    /** Case-insensitive heading patterns (regular expression source) that start this block in a sheet. */
    headingPatterns: string[];
    /** Table column → accepted column headings in the sheet. */
    columns?: Record<string, string[]>;
  };
}

export interface ProfileTemplateDef {
  id: string;
  entityTypeId: string;
  name: string;
  /** "2.0", "2.1"… */
  version: string;
  /** Variant templates inherit this template's blocks, then add/override (`blocks`) and remove (`removedBlocks`). */
  parentTemplateId: string | null;
  blocks: TemplateBlock[];
  removedBlocks: string[];
  /** Header markings that are template boilerplate (e.g. "CONFIDENTIAL — Internal use only"), not classification. */
  boilerplateMarkings: string[];
  active: boolean;
  notes: string | null;
  createdAt: number;
}

export interface EnumValueDef {
  id: string;
  listId: string;
  key: string;
  label: string;
  /** Entity palette slot 1–12 for color rules ("Color by Camp"); null = no color. */
  colorSlot: number | null;
  /** Numeric score (rating scales: 1–5). */
  score: number | null;
  /** Other spellings matched case-insensitively on import ("Low-Medium", "Financial capacity"…). */
  synonyms: string[];
  description: string | null;
  order: number;
  archived: boolean;
}

export interface EnumListDef {
  id: string;
  key: string;
  label: string;
  description: string | null;
  /** Seeded lists that code relies on can't be renamed by key. */
  system: boolean;
  values: EnumValueDef[];
}

export const RELATION_CATEGORIES = ['relation', 'participation', 'location', 'flow'] as const;
export type RelationCategory = (typeof RELATION_CATEGORIES)[number];

export interface RelationTypeDef {
  id: string;
  key: string;
  label: string;
  inverseLabel: string | null;
  category: RelationCategory;
  /** Sub-group within a category, e.g. `vote` for vote roles. */
  groupKey: string | null;
  directed: boolean;
  symmetric: boolean;
  sourceKinds: EntityKind[];
  targetKinds: EntityKind[];
  hasAmount: boolean;
  hasPeriod: boolean;
  description: string | null;
  order: number;
  archived: boolean;
}

/** Everything the UI and importers need, as served by `GET /api/schema`. */
export interface SchemaSnapshot {
  types: EntityTypeDef[];
  groups: FieldGroupDef[];
  fields: FieldDef[];
  templates: ProfileTemplateDef[];
  lists: EnumListDef[];
  relationTypes: RelationTypeDef[];
}

/** Keys: lowercase letters, digits and underscores, starting with a letter. Keys never change once created. */
export const KEY_PATTERN = /^[a-z][a-z0-9_]{0,47}$/;
export const isKey = (k: unknown): k is string => typeof k === 'string' && KEY_PATTERN.test(k);
/** `MED_`, `ORG_`, `POL_`: capitals/digits ending with an underscore. */
export const CODE_PREFIX_PATTERN = /^[A-Z][A-Z0-9]{1,9}_$/;
export const TEMPLATE_VERSION_PATTERN = /^\d+\.\d+$/;

/** Suggests a key from a label ("Level of action" → `level_of_action`). */
export const keyFromLabel = (label: string): string =>
  label
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, 'f_$1')
    .slice(0, 48);
